import {
    useCallback,
    useState,
} from 'react';
import { useIntl } from 'react-intl';

import type { StepComponentProps } from '../../constants';

// Mock tenant data - this would come from an API in real implementation
const mockTenants = [
    {
        id: 'staging',
        name: 'Staging Tenant',
    },
    {
        id: 'production',
        name: 'Production Tenant',
    },
    {
        id: 'development',
        name: 'Development Tenant',
    },
    {
        id: 'testing',
        name: 'Testing Tenant',
    },
];

interface ScopeStepData {
    scope: string;
    selectedTenants: string[];
}

const useGovernancePolicyCreateScopeStepViewModel = ({
    onNext, onBack,
}: StepComponentProps) => {
    const { formatMessage: translate } = useIntl();
    const [ selectedScope, setSelectedScope ] = useState<string>('organization');
    const [ selectedTenants, setSelectedTenants ] = useState<string[]>([]);

    const scopeOptions = [
        {
            label: translate({ id: 'CLIENT_GOVERNANCE_SCOPE_ENTIRE_ORGANIZATION' }),
            value: 'organization',
            'data-cy': 'governance-scope-organization',
        },
        {
            label: translate({ id: 'CLIENT_GOVERNANCE_SCOPE_SELECT_TENANTS' }),
            value: 'tenants',
            'data-cy': 'governance-scope-tenants',
        },
    ];

    const isTenantsSelected = selectedScope === 'tenants';

    const handleScopeChangeLocal = useCallback((value: string) => {
        setSelectedScope(value);
        if (value === 'organization') {
            setSelectedTenants([]);
        }
    }, []);

    const handleTenantChangeLocal = useCallback((selectedValues: string | string[]) => {
        const values = Array.isArray(selectedValues) ? selectedValues : [ selectedValues ];
        setSelectedTenants(values);
    }, []);

    const handleNext = useCallback(() => {
        const data: ScopeStepData = {
            scope: selectedScope,
            selectedTenants: selectedScope === 'tenants' ? selectedTenants : [],
        };
        onNext(data);
    }, [ onNext, selectedScope, selectedTenants ]);

    const handleBack = useCallback(() => {
        if (onBack) {
            onBack();
        }
    }, [ onBack ]);

    const isNextDisabled = isTenantsSelected && selectedTenants.length === 0;

    return {
        // State
        selectedScope,
        selectedTenants,
        isTenantsSelected,
        isNextDisabled,

        // Data
        scopeOptions,
        tenants: mockTenants,

        // Handlers
        handleScopeChange: handleScopeChangeLocal,
        handleTenantChange: handleTenantChangeLocal,
        handleNext,
        handleBack,

        // Computed values
        canProceed: !isNextDisabled,
    };
};

export default useGovernancePolicyCreateScopeStepViewModel;
